import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { NavigationStart, Router, RouterOutlet } from '@angular/router';
import { DialogContainerDirective } from '@progress/kendo-angular-dialog';
import {
  ApiHttpService,
  ApplicationUserService,
  ClientService,
  DetailActionService,
  MercureService,
  NavigationService,
  RouteEntitySubscriberService,
  ScreenService,
  User,
  AppLoadErrorComponent,
  isSessionError,
  SessionWatchService,
} from 'shared';
import { CrowndeskMenuComponent } from '@crowndesk-core/crowndesk-menu/crowndesk-menu.component';
import { CLIENT_MENU, withoutStockSalesReport } from '@crowndesk-core/crowndesk-menu/client-menu/client-menu';
import { SUPPLIER_MENU } from '@crowndesk-core/crowndesk-menu/supplier-menu/supplier-menu';
import {
  CrowndeskAccessRight,
  filterMenuByAccessRights,
  hasAccessRight,
  isRoyalCrownEmployee,
  isSupplierEmployee,
  isSupplierEmployeePage,
  SUPPLIER_EMPLOYEE_HOME,
} from '@crowndesk-core/access-rights/access-rights';
import { StockSalesReportService } from '@features/my-numbers/stock-sales-report/stock-sales-report.service';
import { LoginAsComponent } from '@crowndesk-core/login-as/login-as.component';
import { ImpersonationService } from '@crowndesk-core/login-as/impersonation.service';
import { AuthService } from '@auth0/auth0-angular';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, filter, first, skip } from 'rxjs/operators';
import { catchError, combineLatest, map, of, switchMap } from 'rxjs';
import { TopBarComponent } from '@crowndesk-core/top-bar/top-bar.component';
import { CustomerViewService } from '@crowndesk-core/customer-view/customer-view.service';
import { SkeletonComponent } from '@progress/kendo-angular-indicators';

@Component({
  selector: 'rc-crowndesk',
  standalone: true,
  imports: [RouterOutlet, DialogContainerDirective, CrowndeskMenuComponent, TopBarComponent, SkeletonComponent, LoginAsComponent, AppLoadErrorComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  title = 'crowndesk';

  #authService = inject(AuthService);
  #apiService = inject(ApiHttpService);
  #detailActionService = inject(DetailActionService);
  #applicationUserService = inject(ApplicationUserService);
  #navigationService = inject(NavigationService);
  #mercureService = inject(MercureService);
  #destroyRef = inject(DestroyRef);
  #screenService = inject(ScreenService);
  #router = inject(Router);
  #clientService = inject(ClientService);
  #tracker = inject(RouteEntitySubscriberService);
  protected customerView = inject(CustomerViewService);
  #impersonationService = inject(ImpersonationService);
  #sessionWatch = inject(SessionWatchService);
  userSettings = toSignal(this.#applicationUserService.userSettings$);
  user = signal<User | null>(null);
  #stockSalesReportService = inject(StockSalesReportService);
  /** Whether a supplier takes Stock & Sales Reports from an employee who may send them. */
  #canReportStockAndSales = toSignal(
    this.#applicationUserService.user$.pipe(
      map((user) => !!user && !isRoyalCrownEmployee(user) && hasAccessRight(user, CrowndeskAccessRight.BILLING)),
      distinctUntilChanged(),
      switchMap((allowed) =>
        allowed
          ? this.#stockSalesReportService.getSuppliers().pipe(
              map((suppliers) => suppliers.length > 0),
              catchError(() => of(false)),
            )
          : of(false),
      ),
    ),
    { initialValue: false },
  );
  menu = computed(() =>
    isSupplierEmployee(this.user())
      ? SUPPLIER_MENU
      : filterMenuByAccessRights(
          this.#canReportStockAndSales() ? CLIENT_MENU : withoutStockSalesReport(CLIENT_MENU),
          this.user(),
        ),
  );
  isRoyalCrownEmployee = computed(() => isRoyalCrownEmployee(this.user()));
  appReady = signal(false);
  userLoadError = this.#applicationUserService.loadError;
  /** The signed-out page is public: it renders on its own, without the app shell or the user. */
  isOnSignedOutPage = this.#sessionWatch.isOnSignedOutPage;
  readonly isMobile = computed(() => this.#screenService.isMobile());
  readonly isTablet = computed(() => this.#screenService.isTablet());
  // user = toSignal(this.#applicationUserService.user$);

  constructor() {
    this.#sessionWatch.watch();
    this.#applicationUserService.user$
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((user) => this.#showUser(user));
    // There is no session to load a user for on the signed-out page
    if (!this.isOnSignedOutPage()) {
      this.#loadApplicationUser();
    }
    toObservable(this.#impersonationService.clientEmployeeId)
      .pipe(skip(1), distinctUntilChanged(), takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.#reloadApplicationUser());

    this.#navigationService.updateDetailsActionEmitter.subscribe((value) => {
      if (value) {
        this.#mercureService.destroyEventEmitter.emit(true);
      }
    });

    this.#router.events
      .pipe(
        filter((e): e is NavigationStart => e instanceof NavigationStart),
        skip(1),
        map((e) => e.url),
        distinctUntilChanged(),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => {
        if (this.#navigationService.getLastUrl() !== this.#navigationService.getCurrentUrl()) {
          this.#detailActionService.clear();
        }
      });

    // effect(() => {
    //   if (this.user()) {
    //     this.#translateService.setDefaultLang(this.user()!.settings.language);
    //   }
    // });
  }

  /**
   * The first page (by default the dashboard) is opened before the user is known: a supplier employee is first sent to
   * their own part, so the client page never shows (or loads) for them.
   */
  #showUser(user: User | null): void {
    if (isSupplierEmployee(user) && !isSupplierEmployeePage(this.#router.url)) {
      this.#router.navigateByUrl(SUPPLIER_EMPLOYEE_HOME, { replaceUrl: true }).finally(() => this.user.set(user));
      return;
    }
    this.user.set(user);
  }

  /** Starting or stopping an impersonation changes who /users/me returns, so load the user again. */
  #reloadApplicationUser(): void {
    this.appReady.set(false);
    this.#applicationUserService.userSubject.next(null);
    this.#loadApplicationUser();
  }

  /** Loads the user again after a failure; the pending /cookie subscription picks it up. */
  retryLoadUser(): void {
    this.#fetchApplicationUser();
  }

  #loadApplicationUser(): void {
    this.#fetchApplicationUser();

    // When both auth and user are ready, call /cookie ONCE and then track entities
    combineLatest([this.#authService.isAuthenticated$, this.#applicationUserService.user$])
      .pipe(
        filter(([isAuth, user]) => !!isAuth && !!user),
        first(),
        switchMap(() => this.#apiService.get('/cookie')), // ✅ returns Observable
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => {
        // this.#tracker.start(); // if you ever want this again
        this.appReady.set(true);
      });
  }

  #fetchApplicationUser(): void {
    this.#applicationUserService.setApplicationUser((err) => {
      // a failed impersonation must not log the Royal Crown employee out: dropping it reloads them as themselves
      if (this.#impersonationService.clientEmployeeId()) {
        this.#impersonationService.stop();
        return;
      }
      // any other failure keeps the user on the page, where the load error offers a retry
      if (isSessionError(err)) {
        this.#sessionWatch.toSignedOut('expired', location.pathname + location.search);
      }
    });
  }
}
